import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase';

// Generate unique account number (EX-XXXXXX format)
function generateAccountNumber() {
  const timestamp = Date.now().toString();
  const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  const uniquePart = timestamp.slice(-3);
  return `EX-${uniquePart}${randomPart}`;
}

const AuthContext = createContext();

export { AuthContext };
export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function with Zoho Catalyst DataStore integration
  async function signup(email, password, userData) {
    try {
      console.log('🚀 Starting signup process...');
      console.log('📧 Email:', email);
      console.log('🔐 Password length:', password.length);
      console.log('👤 User data:', userData);
      
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Generate unique account number
      const accountNumber = generateAccountNumber();
      
      // Store account number in localStorage for immediate access
      localStorage.setItem('exodia_account_number', accountNumber);
      localStorage.setItem('exodia_user_email', email);
      
      // Update Firebase profile with full name, custom data, and account number
      await updateProfile(result.user, {
        displayName: `${userData.firstName} ${userData.lastName}`,
        photoURL: `data:application/json;base64,${btoa(JSON.stringify({
          companyName: userData.companyName || '',
          phoneNumber: userData.phoneNumber || '',
          country: userData.country || '',
          industry: userData.industry || '',
          accountNumber: accountNumber
        }))}`
      });
      
      // Send email verification
      console.log('📧 Attempting to send email verification...');
      try {
        await sendEmailVerification(result.user);
        console.log('✅ Email verification sent successfully!');
        console.log('📧 Verification email sent to:', result.user.email);
      } catch (verificationError) {
        console.error('❌ Failed to send email verification:', verificationError);
        throw new Error(`Email verification failed: ${verificationError.message}`);
      }
      
                   // Store user data in Zoho Catalyst DataStore
      await storeUserDataInCatalyst(result.user.uid, {
        email: email, // Explicitly pass email
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber,
        companyName: userData.companyName,
        country: userData.country,
        industry: userData.industry,
        createdAt: new Date().toISOString(), // This will be converted to datetime in backend
        emailVerified: false,
        screeningCredits: 0, // New users start with 0 screening credits
        talentRolePass: 0, // New users start with 0 talent role passes
        shortlistPass: 0 // New users start with 5 shortlist passes
      });
      
      // Sign out the user after signup (they need to verify email first)
      await signOut(auth);
      
      return result;
    } catch (error) {
      throw error;
    }
  }

     // Function to store user data in Zoho Catalyst DataStore
   async function storeUserDataInCatalyst(userId, userData) {
     try {
       console.log('🚀 Attempting to store user data in Catalyst...');
       console.log('📤 User ID:', userId);
       console.log('📤 User Data:', userData);
       
             // Validate required fields (only basic info required)
      if (!userId || !userData.email || !userData.firstName || !userData.lastName || !userData.phoneNumber) {
        throw new Error('Missing required user data fields');
      }
       
             const requestBody = {
        user_id: userId,
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        phone_number: userData.phoneNumber,
        company_name: userData.companyName || '',
        country: userData.country || '',
        industry: userData.industry || '',
        created_at: userData.createdAt,
        email_verified: userData.emailVerified,
        screening_credits: userData.screeningCredits || 10,
        talent_role_pass: userData.talentRolePass || 3,
        shortlist_pass: userData.shortlistPass || 5
      };
       
       console.log('📤 Request Body being sent:', requestBody);
       
       const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user', {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify(requestBody)
       });
       
       console.log('📥 Catalyst response status:', response.status);
       
       if (!response.ok) {
         const errorText = await response.text();
         console.error('❌ Response not OK:', response.status, errorText);
         throw new Error(`Catalyst API error: ${response.status} - ${errorText}`);
       }
       
       const result = await response.json();
       console.log('✅ Successfully stored user data in Catalyst:', result);
       
       return result;
     } catch (error) {
       console.error('❌ Error storing user data in Catalyst:', error);
       // CRITICAL: Always throw the error so the user knows what failed
       throw error;
     }
   }

  // Login function with email verification check
  async function login(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email is verified
      if (!result.user.emailVerified) {
        // Sign out the user if email is not verified
        await signOut(auth);
        throw new Error('Please verify your email before signing in. Check your inbox for the verification link.');
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Logout function - THIS WILL ACTUALLY WORK!
  function logout() {
    return signOut(auth);
  }

  // Password reset function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Email verification function
  function verifyEmail() {
    return sendEmailVerification(currentUser);
  }

     // Test function to verify Catalyst connection
   async function testCatalystConnection() {
     try {
       console.log('🔍 Testing Catalyst connection...');
       const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/');
       
       if (response.ok) {
         const result = await response.text();
         console.log('✅ Catalyst connection successful:', result);
         return true;
       } else {
         console.error('❌ Catalyst connection failed:', response.status);
         return false;
       }
     } catch (error) {
       console.error('❌ Catalyst connection error:', error);
       return false;
     }
   }

     // Resend verification email function
  async function resendVerificationEmail(email) {
    try {
      console.log('📧 Attempting to resend verification email to:', email);
      
      // We need to get the user object without requiring password
      // Since the user is not verified, we'll use a different approach
      const user = auth.currentUser;
      
      if (user && user.email === email) {
        // User is already signed in
        console.log('✅ User already signed in:', user.email);
        await sendEmailVerification(user);
        console.log('✅ Verification email resent successfully!');
        return true;
      } else {
        // Try to get user by email (this might not work without password)
        console.log('⚠️ User not signed in, attempting alternative method...');
        throw new Error('Please sign in again to resend verification email');
      }
    } catch (error) {
      console.error('❌ Failed to resend verification email:', error);
      throw error;
    }
  }

  // Get user credits from database
  async function getUserCredits(userId) {
    try {
      const url = `https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user-credits?userId=${encodeURIComponent(userId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get user credits: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error getting user credits:', error);
      throw error;
    }
  }

  // Update user credits in database
  async function updateUserCredits(userId, screeningCredits, talentRolePass, shortlistPass, unlockedTalentIds = null) {
    try {
      const requestBody = {
        user_id: userId,
        screening_credits: screeningCredits,
        talent_role_pass: talentRolePass,
        shortlist_pass: shortlistPass
      };
      
      // Include unlocked talent IDs if provided
      if (unlockedTalentIds !== null) {
        requestBody.unlockedTalentIds = unlockedTalentIds;
      }
      
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user-credits', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update user credits: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating user credits:', error);
      throw error;
    }
  }

  // Create transaction in database
  async function createTransaction(transactionData) {
    try {
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user-transactions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create transaction: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  }

  // Get user transactions from database
  async function getUserTransactions(userId) {
    try {
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user-transactions?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get user transactions: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting user transactions:', error);
      throw error;
    }
  }




  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

     const value = {
     currentUser,
     loading,
     signup,
     login,
     logout,
     resetPassword,
     verifyEmail,
     resendVerificationEmail,
     testCatalystConnection,
     getUserCredits,
     updateUserCredits,
     createTransaction,
     getUserTransactions
   };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
